import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Search, BadgeCheck, Lock, Star, MapPin, Clock, Heart, Users, MessageCircle, Calendar } from "lucide-react";
import { findCityBySlug, SEO_CITIES } from "@/lib/seoCities";

const SITE = "https://clickandgard.fr";

const CityLanding = () => {
  const { ville } = useParams<{ ville: string }>();
  const city = ville ? findCityBySlug(ville) : undefined;

  if (!city) return <Navigate to="/babysitter-france" replace />;

  const path = `/baby-sitter/${city.slug}`;
  const title = `Baby-sitter à ${city.name} — Nounou & garde d'enfants vérifiée`;
  const description = `Trouvez une baby-sitter ou nounou à ${city.name} (${city.region}). Profils vérifiés, réservation en ligne, 0% commission. Garde d'enfants à domicile, en urgence ou périscolaire.`;

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Baby-sitter France", item: `${SITE}/babysitter-france` },
      { "@type": "ListItem", position: 3, name: `Baby-sitter ${city.name}`, item: `${SITE}${path}` },
    ],
  };

  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `Click&Gard — Baby-sitter à ${city.name}`,
    description,
    url: `${SITE}${path}`,
    image: `${SITE}/og-image.jpg`,
    areaServed: { "@type": "City", name: city.name, addressRegion: city.region, addressCountry: "FR" },
    priceRange: "€€",
    serviceType: "Garde d'enfants",
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `Combien coûte une baby-sitter à ${city.name} ?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `À ${city.name}, le tarif d'une baby-sitter se situe en moyenne entre 10 € et 15 € de l'heure, selon l'expérience, les diplômes et la prestation demandée. Sur Click&Gard, chaque professionnel fixe librement son tarif et vous voyez le prix avant de réserver.`,
        },
      },
      {
        "@type": "Question",
        name: `Comment trouver une nounou en urgence à ${city.name} ?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `Utilisez le filtre « Disponible immédiatement » de Click&Gard pour voir les baby-sitters de ${city.name} prêtes à intervenir aujourd'hui ou ce soir. Vous pouvez les contacter via la messagerie et obtenir une réponse en quelques minutes.`,
        },
      },
      {
        "@type": "Question",
        name: `Les baby-sitters à ${city.name} sont-elles vérifiées ?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `Oui. Chaque nounou inscrite sur Click&Gard à ${city.name} passe une vérification d'identité (pièce + selfie) et une validation manuelle par notre équipe avant de pouvoir recevoir des demandes.`,
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-immersive">
      <Seo title={title} description={description} path={path} jsonLd={[breadcrumbJsonLd, localBusinessJsonLd, faqJsonLd]} />
      <Navbar />

      <section className="relative overflow-hidden py-16 md:py-24 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />
        <div className="relative max-w-5xl mx-auto text-center">
          <nav aria-label="Fil d'Ariane" className="text-sm text-muted-foreground mb-4">
            <Link to="/" className="hover:text-primary">Accueil</Link>
            <span className="mx-2">›</span>
            <Link to="/babysitter-france" className="hover:text-primary">France</Link>
            <span className="mx-2">›</span>
            <span className="text-foreground">{city.name}</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <MapPin className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-primary">{city.region}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
            Baby-sitter à {city.name}
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Trouvez la nounou idéale à {city.name} : profils vérifiés, avis de parents, 0% commission. Réservez votre garde d'enfants en quelques clics.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/search?ville=${encodeURIComponent(city.name)}`}>
              <Button size="lg" variant="neon" className="text-base font-semibold">
                <Search className="w-5 h-5" />
                Voir les baby-sitters à {city.name}
              </Button>
            </Link>
            <Link to="/auth?tab=register">
              <Button size="lg" variant="outline" className="text-base font-semibold">Devenir baby-sitter</Button>
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-primary" /> Profils vérifiés</span>
            <span className="flex items-center gap-2"><Lock className="w-4 h-4 text-primary" /> 0% commission</span>
            <span className="flex items-center gap-2"><Heart className="w-4 h-4 text-primary" /> 100% gratuit pour les parents</span>
          </div>
        </div>
      </section>

      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto prose prose-invert prose-lg">
          <article className="space-y-6 text-foreground/90">
            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground">
              Garde d'enfants à {city.name} : la solution Click&Gard
            </h2>
            <p>
              Click&Gard est la plateforme de référence pour trouver une <strong>baby-sitter à {city.name}</strong>. Avec une population de {city.population} d'habitants, {city.name} est {city.highlight}. Notre algorithme de géolocalisation vous permet de trouver en quelques secondes les <strong>nounous</strong> et professionnels de la <strong>garde d'enfants</strong> les plus proches de votre domicile.
            </p>
            <p>
              Que vous cherchiez une garde ponctuelle pour une soirée, une garde régulière en sortie d'école, une nounou pour les vacances scolaires ou une <strong>baby-sitter en urgence</strong> à {city.name}, vous trouverez sur Click&Gard des profils variés correspondant à vos besoins. Tous les professionnels sont vérifiés (identité, diplômes) et leurs tarifs sont affichés en toute transparence.
            </p>

            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground pt-4">
              Comment réserver une nounou à {city.name} ?
            </h2>
            <p>
              Le processus de réservation est simple et rapide. Créez un compte parent gratuit, indiquez le créneau souhaité, le nombre d'enfants et le quartier de {city.name} concerné. Parcourez les profils disponibles, consultez les avis vérifiés laissés par d'autres parents de {city.region}, échangez via la messagerie sécurisée puis réservez en ligne. Le règlement de la garde se fait ensuite directement entre vous et la nounou.
            </p>

            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground pt-4">
              Tarifs d'une baby-sitter à {city.name}
            </h2>
            <p>
              Le tarif horaire d'une baby-sitter à {city.name} varie généralement entre 10 € et 15 € selon l'expérience et les prestations demandées (sortie d'école, repas, devoirs, bain, garde de plusieurs enfants). Sur Click&Gard, vous voyez le tarif avant de réserver, sans frais cachés. Click&Gard est 100% gratuit pour les parents.
            </p>

            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground pt-4">
              Garde d'enfants à domicile ou chez la nounou
            </h2>
            <p>
              Selon vos préférences, vous pouvez choisir une <strong>garde à domicile à {city.name}</strong> (la baby-sitter se déplace chez vous) ou une garde au domicile de la nounou. Certains professionnels proposent également des activités en extérieur : sorties au parc, accompagnement à la sortie d'école, activités sportives ou culturelles.
            </p>
          </article>
        </div>
      </section>

      <section className="py-12 px-4 bg-card/30 border-y border-border/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-10">Pourquoi Click&Gard à {city.name} ?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: BadgeCheck, title: "Profils vérifiés", desc: `Identité et diplômes contrôlés pour chaque baby-sitter à ${city.name}.` },
              { icon: Lock, title: "0% commission", desc: "Vous réglez la nounou directement, sans intermédiaire." },
              { icon: Star, title: "Avis vérifiés", desc: "Notes et commentaires laissés par les parents de la région." },
              { icon: Clock, title: "Disponibilité immédiate", desc: "Filtrez les baby-sitters disponibles dès aujourd'hui." },
              { icon: MapPin, title: "Géolocalisation précise", desc: `Affichage des nounous proches de votre quartier de ${city.name}.` },
              { icon: Heart, title: "100% gratuit pour les parents", desc: "Aucun abonnement, aucun frais caché." },
            ].map((item) => (
              <article key={item.title} className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-4 shadow-lg">
                  <item.icon className="w-6 h-6 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-10">Comment ça marche ?</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { n: "1", icon: Users, title: "Inscription gratuite", desc: "Créez votre compte parent en moins d'une minute." },
              { n: "2", icon: Search, title: `Recherche à ${city.name}`, desc: "Filtrez par créneau, tarif et services proposés." },
              { n: "3", icon: MessageCircle, title: "Échange & devis", desc: "Discutez avec la nounou avant de confirmer." },
              { n: "4", icon: Calendar, title: "Réservation en ligne", desc: "Vous réglez la nounou directement après la garde." },
            ].map((step) => (
              <div key={step.n} className="relative text-center">
                <div className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary text-primary-foreground font-bold flex items-center justify-center shadow-lg">
                  {step.n}
                </div>
                <div className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm p-6 h-full">
                  <step.icon className="w-10 h-10 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 px-4 bg-card/30 border-y border-border/50">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-display font-bold mb-6">Découvrir Click&Gard dans d'autres villes</h2>
          <div className="flex flex-wrap gap-2 justify-center">
            {SEO_CITIES.filter((c) => c.slug !== city.slug).slice(0, 18).map((c) => (
              <Link key={c.slug} to={`/baby-sitter/${c.slug}`} className="px-4 py-2 rounded-full bg-card/50 border border-border/50 text-sm hover:border-primary/40 hover:text-primary transition-all">
                Baby-sitter {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">Prêt à trouver votre baby-sitter à {city.name} ?</h2>
          <p className="text-lg text-muted-foreground mb-8">Rejoignez les parents qui font confiance à Click&Gard pour la garde de leurs enfants.</p>
          <Link to={`/search?ville=${encodeURIComponent(city.name)}`}>
            <Button size="lg" variant="neon" className="text-base font-semibold">
              <Search className="w-5 h-5" />
              Voir les profils disponibles
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CityLanding;
